import * as React from "react";
import { EventVendorQuery } from "@src/@types/api.d";
import { Mail } from "lucide-react";
import { motion } from "framer-motion";
import Icon from "@src/components/Icon";
import { decodeHtmlEntities } from "@src/utils/general";

type Props = {
  eventId: number;
  eventVendorData: EventVendorQuery;
};

const Vendors: React.FC<Props> = ({ eventVendorData }) => {
  const vendors = (eventVendorData?.eventVendor ?? []).filter(
    (vendor): vendor is NonNullable<typeof vendor> => Boolean(vendor)
  );
  const sectionRef = React.useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = React.useState(0);
  const ITEMS_PER_PAGE = 3;
  const totalPages = Math.ceil(vendors.length / ITEMS_PER_PAGE);

  if (!vendors.length) {
    return null;
  }

  const startIdx = currentPage * ITEMS_PER_PAGE;
  const visibleVendors = vendors.slice(startIdx, startIdx + ITEMS_PER_PAGE);

  const scrollToSection = () => {
    setTimeout(() => {
      sectionRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 50);
  };

  const goToNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage((prev) => prev + 1);
      scrollToSection();
    }
  };

  const goToPrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage((prev) => prev - 1);
      scrollToSection();
    }
  };

  return (
    <section ref={sectionRef} className="w-full py-6 md:py-12 bg-[#E8EEF6]">
      <div className="max-w-5xl mx-auto px-4 md:px-8 lg:px-20">
        <div className="mb-8 md:mb-10 text-center">
          <h3 className="font-bauerBodoni text-2xl md:text-4xl font-light text-[#071A2F] uppercase tracking-wider">
            Our Vendors
          </h3>
        </div>

        <article className="border border-[#fff] p-2 relative">
          {totalPages > 1 && (
            <>
              <motion.button
                onClick={goToPrevPage}
                disabled={currentPage === 0}
                className="hidden lg:flex absolute -left-[100px] top-1/2 -translate-y-1/2 z-20 w-[50px] h-[50px] rounded-full bg-[#031d40] items-center justify-center transition-all duration-200 hover:scale-105 hover:bg-[#031d40]/90 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Previous vendors"
              >
                <svg
                  width="24"
                  height="20"
                  viewBox="0 0 56 87"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ transform: "scaleX(-1)" }}
                >
                  <g>
                    <line
                      transform="rotate(101.38104248046875 27.75 63.25)"
                      y2="83.75"
                      x2="53.75"
                      y1="42.75"
                      x1="1.75"
                      strokeWidth="5"
                      stroke="#b18958"
                      fill="none"
                    />
                    <line
                      y2="41.75"
                      x2="52.75"
                      y1="0.75"
                      x1="0.75"
                      strokeWidth="5"
                      stroke="#b18958"
                      fill="none"
                    />
                  </g>
                </svg>
              </motion.button>

              <motion.button
                onClick={goToNextPage}
                disabled={currentPage === totalPages - 1}
                className="hidden lg:flex absolute -right-[100px] top-1/2 -translate-y-1/2 z-20 w-[50px] h-[50px] rounded-full bg-[#031d40] items-center justify-center transition-all duration-200 hover:scale-105 hover:bg-[#031d40]/90 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed"
                aria-label="Next vendors"
              >
                <svg
                  width="24"
                  height="20"
                  viewBox="0 0 56 87"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g>
                    <line
                      transform="rotate(101.38104248046875 27.75 63.25)"
                      y2="83.75"
                      x2="53.75"
                      y1="42.75"
                      x1="1.75"
                      strokeWidth="5"
                      stroke="#b18958"
                      fill="none"
                    />
                    <line
                      y2="41.75"
                      x2="52.75"
                      y1="0.75"
                      x1="0.75"
                      strokeWidth="5"
                      stroke="#b18958"
                      fill="none"
                    />
                  </g>
                </svg>
              </motion.button>
            </>
          )}

          <div className="bg-white border border-[#b18958]/40 shadow-xl p-6 md:p-8">
            <div className="grid grid-cols-1 gap-4">
              {visibleVendors.map((vendor, index) => {
                const decodedName = decodeHtmlEntities(vendor.name);
                const decodedRole = decodeHtmlEntities(vendor.role);
                const decodedEmail = decodeHtmlEntities(vendor.email);
                const decodedInstagramHandle = decodeHtmlEntities(
                  vendor.instagram_handle
                ).trim();
                const normalizedHandle = decodedInstagramHandle
                  .replace(/^@/, "")
                  .replace(/^https?:\/\/www\.instagram\.com\//i, "")
                  .replace(/\/$/, "");
                const instagramHref = normalizedHandle
                  ? `https://www.instagram.com/${normalizedHandle}/`
                  : "";

                return (
                  <div
                    key={`vendors-home-page-${index}`}
                    className="w-full p-5 md:p-6 border border-[#b18958] bg-[#F7FAFF] rounded-2xl"
                  >
                    <div className="font-bauerBodoni text-2xl md:text-3xl text-[#071A2F] uppercase tracking-wide leading-tight">
                      {decodedName}
                    </div>

                    <p className="mt-1 font-openSans font-medium text-sm md:text-base text-[#123B66] uppercase tracking-widest">
                      {decodedRole}
                    </p>

                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[#071A2F]">
                      {!!decodedEmail && (
                        <p className="font-openSans font-normal text-sm md:text-base text-[#071A2F] opacity-80 inline-flex items-center gap-1.5 break-all">
                          <Mail
                            className="w-4 h-4 text-[#071A2F]"
                            aria-hidden="true"
                          />
                          {decodedEmail}
                        </p>
                      )}

                      {!!instagramHref && (
                        <a
                          target="_blank"
                          href={instagramHref}
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-[#071A2F] hover:opacity-80 transition-opacity"
                        >
                          <span
                            className="w-5 h-5 inline-flex items-center justify-center flex-shrink-0 [&_svg]:w-5 [&_svg]:h-5 [&_svg]:block"
                            aria-hidden="true"
                          >
                            <Icon iconName="instagram" />
                          </span>
                          <span className="font-openSans text-sm md:text-base">
                            {decodedInstagramHandle}
                          </span>
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {totalPages > 1 && (
              <div className="flex lg:hidden justify-center items-center gap-4 mt-6 pt-2">
                <motion.button
                  onClick={goToPrevPage}
                  disabled={currentPage === 0}
                  className="w-[50px] h-[50px] rounded-full bg-[#031d40] flex items-center justify-center transition-all duration-200 hover:scale-105 hover:bg-[#031d40]/90 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Previous vendors"
                >
                  <svg
                    width="24"
                    height="20"
                    viewBox="0 0 56 87"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ transform: "scaleX(-1)" }}
                  >
                    <g>
                      <line
                        transform="rotate(101.38104248046875 27.75 63.25)"
                        y2="83.75"
                        x2="53.75"
                        y1="42.75"
                        x1="1.75"
                        strokeWidth="5"
                        stroke="#b18958"
                        fill="none"
                      />
                      <line
                        y2="41.75"
                        x2="52.75"
                        y1="0.75"
                        x1="0.75"
                        strokeWidth="5"
                        stroke="#b18958"
                        fill="none"
                      />
                    </g>
                  </svg>
                </motion.button>

                <motion.button
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages - 1}
                  className="w-[50px] h-[50px] rounded-full bg-[#031d40] flex items-center justify-center transition-all duration-200 hover:scale-105 hover:bg-[#031d40]/90 active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Next vendors"
                >
                  <svg
                    width="24"
                    height="20"
                    viewBox="0 0 56 87"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g>
                      <line
                        transform="rotate(101.38104248046875 27.75 63.25)"
                        y2="83.75"
                        x2="53.75"
                        y1="42.75"
                        x1="1.75"
                        strokeWidth="5"
                        stroke="#b18958"
                        fill="none"
                      />
                      <line
                        y2="41.75"
                        x2="52.75"
                        y1="0.75"
                        x1="0.75"
                        strokeWidth="5"
                        stroke="#b18958"
                        fill="none"
                      />
                    </g>
                  </svg>
                </motion.button>
              </div>
            )}
          </div>
        </article>
      </div>
    </section>
  );
};

export default Vendors;
